import { apiClient } from "./apiClient";

/**
 * Mirrors the bridge-models {@code FlowReachoutMessageIn} envelope. We do
 * NOT declare a parallel UI-only DTO — the wire shape is owned by
 * bridge-models and atlas reuses it directly.
 */
export type ReachoutChannelType = "WHATSAPP" | "SMS" | "EMAIL";

export type LeadIntent =
  | "AGENT_INIT_PROPERTY_RECOMMEND"
  | "AGENT_INIT_PROPERTY_RENT"
  | "AGENT_INIT_PROPERTY_SALES";

export interface ReachoutMessage {
  waMessageId?: string | null;
  messageType?: string;
  content?: string | null;
  messageId?: string;
  /** WhatsApp template name when {@link messageType} === "template". */
  templateName?: string;
  /** Ordered values for the {1}..{N} placeholders in the template body. */
  templateParams?: string[];
  /** Optional media URL to attach to the message (e.g. property image). */
  imageUrl?: string | null;
}

/**
 * Atlas-originated reach-out payload. Field names must match the
 * bridge-models {@code FlowReachoutMessageIn} record exactly — atlas's
 * Jackson deserialiser drops unknown fields silently, which is how the
 * earlier {@code contactPhoneNumber} / {@code branchIds} version reached
 * the validate() step looking empty.
 *
 * <p>Server-managed fields (don't send from UI):
 * <ul>
 *   <li>{@code channelId} — resolved from communication_channels.</li>
 *   <li>{@code conversationId} — atlas nulls it; a fresh reach-out starts
 *       a new conversation.</li>
 *   <li>{@code metadata} — atlas nulls it; not forwarded downstream.</li>
 * </ul>
 */
export interface FlowReachoutMessageIn {
  channelType: ReachoutChannelType;
  /** Atlas contacts.id of the selected contact — UI sends this directly so
   *  atlas doesn't have to fuzzy-resolve by phone/email (phone format drift
   *  has bitten us before, e.g. "447…" vs "+447…"). */
  contactId?: string | null;
  /** E.164 phone for WhatsApp / SMS; optional for email-only contacts. */
  personPhoneNumber?: string | null;
  /** Email for EMAIL channels; optional for phone-only contacts. */
  personEmailAddress?: string | null;
  messages: ReachoutMessage[];
  legalEntityId: string;
  /** Singular — null/empty for LE-scoped reach-outs. */
  branchId?: string | null;
  timestamp?: string;
  /** Sent as a String so the bridge-models DTO stays loose. */
  leadIntent: LeadIntent;
  propertyId?: string | null;
}

const BRIDGE_LOGO_PATH = "/bridge-logo.svg";

/** Public Bridge logo URL for WhatsApp template header images. */
export function resolveReachOutTemplateImageUrl(): string {
  return new URL(BRIDGE_LOGO_PATH, window.location.origin).href;
}

export const reachoutService = {
  /**
   * Publishes a reach-out envelope. Atlas resolves the channelId + contactId
   * server-side and nulls conversationId / metadata before publishing.
   * Errors come back with stable {@code code} values:
   *   CHANNEL_NOT_FOUND, CHANNEL_NOT_ACTIVE, WHATSAPP_NOT_ENABLED,
   *   CHANNEL_TYPE_UNSUPPORTED, LEGAL_ENTITY_REQUIRED, CONTACT_REQUIRED, etc.
   */
  publish: (body: FlowReachoutMessageIn) =>
    apiClient.post("/reach-out", body).then((r) => r.data),

  /**
   * Calls the backend to securely parse, validate, and convert a contact's phone
   * number into E.164 format. If the backend deems it completely invalid,
   * `isValid` will be false.
   */
  formatPhone: (phone: string, overrideCountryCode?: string) =>
    apiClient
      .get<{
        hasCountryCode: boolean;
        originalPhone: string;
        formattedPhone: string | null;
        isValid: boolean;
      }>("/reach-out/format-phone", {
        params: { phone, overrideCountryCode },
      })
      .then((r) => r.data),
};

export const LEAD_INTENT_OPTIONS: { value: LeadIntent; label: string }[] = [
  { value: "AGENT_INIT_PROPERTY_RECOMMEND", label: "Agent — Property Recommend" },
  { value: "AGENT_INIT_PROPERTY_RENT", label: "Agent — Property Rent" },
  { value: "AGENT_INIT_PROPERTY_SALES", label: "Agent — Property Sales" },
];

export const CHANNEL_TYPE_OPTIONS: { value: ReachoutChannelType; label: string }[] = [
  { value: "WHATSAPP", label: "WhatsApp" },
  { value: "SMS", label: "SMS" },
  { value: "EMAIL", label: "Email" },
];